import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Loader } from "@/components/Loader";
import { TopLogoBar, BottomTabs } from "@/components/AppShell";
import { TextSizeToggle } from "@/components/TextSizeToggle";
import { DayContent } from "@/components/DayContent";
import { EngineDay, StatusPill } from "@/components/c2g/EngineDay";
import { postAction } from "@/components/c2g/OggiEngine";

export default function DayView() {
  const { day } = useParams();
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [options, setOptions] = useState([]);
  const [dateLabel, setDateLabel] = useState("");
  const [progress, setProgress] = useState({});
  const [saving, setSaving] = useState(null);
  const [view, setView] = useState(null);

  useEffect(() => {
    api.get(`/path/day/${day}`).then(({ data }) => {
      if (data.engine) return setView(data);
      setPayload(data.day);
      setOptions(data.options || []);
      setDateLabel(data.date_label || "");
      setProgress(data.progress || {});
    }).catch((e) => {
      if (e.response?.status === 403) toast.error("Questo giorno si apre più avanti");
      navigate("/percorso", { replace: true });
    });
  }, [day]); // eslint-disable-line

  if (view) {
    const onAction = async (p) => {
      const data = await postAction(view.day_number, p);
      if (data) setView(data);
      return !!data;
    };
    return (
      <>
        <div className="min-h-screen px-5 pb-24 pt-6" data-testid="day-view-page">
          <TopLogoBar onBack={() => navigate("/percorso")} testid="day-back-button" />
          <p className="text-sm font-bold uppercase tracking-wide text-[#F4F4F5]" data-testid="day-view-profile">{view.profile.dimension} <span className="ml-1 rounded-md border border-[#D4AF37] px-1.5 py-0.5 text-[12px] text-[#E5C158]">{view.profile.level_label}</span></p>
          <p className="mt-2 text-[12px] font-semibold uppercase tracking-wider text-[#8A8E96]" data-testid="day-view-date">Giorno {view.day_number} di 7 · {view.date_label}</p>
          <h1 className="font-serif-el text-2xl font-semibold leading-tight text-[#FAF9F6]">{view.day.title}</h1>
          <StatusPill view={view} />
          {!view.editable && <p className="mt-2 text-sm text-[#8A8E96]" data-testid="day-view-readonly">Giornata in sola lettura.</p>}
          <div className="mt-6"><EngineDay view={view} onAction={onAction} /></div>
        </div>
        <BottomTabs />
      </>
    );
  }
  if (!payload) return <Loader />;

  const toggle = async (key) => {
    setSaving(key);
    try {
      const { data } = await api.post(`/path/day/${day}/moment`, { moment: key, done: !progress[key] });
      const dp = data.progress[String(day)] || {};
      setProgress(dp);
      if (dp.completed) toast.success("Giorno completato! 🔥");
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(null);
    }
  };

  const interact = async (moment, data) => {
    try {
      const res = await api.post(`/path/day/${day}/interact`, { moment, data });
      const dp = res.data.progress[String(day)] || {};
      if (dp.completed && !progress.completed) toast.success(dp.action_status === "completed" ? "Giornata vissuta: ottimo lavoro!" : "Giornata chiusa. Domani riparti da qui.");
      setProgress(dp);
      return true;
    } catch {
      toast.error("Errore nel salvataggio");
      return false;
    }
  };

  return (
    <>
      <div className="min-h-screen px-5 pb-24 pt-6" data-testid="day-view-page">
        <TopLogoBar onBack={() => navigate("/percorso")} testid="day-back-button" />
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <p className="text-[12px] font-semibold uppercase tracking-wider text-[#8A8E96]" data-testid="day-view-date">{dateLabel || `Giorno ${payload.day}`}</p>
            <h1 className="font-serif-el text-2xl font-semibold leading-tight text-[#FAF9F6]">{payload.title}</h1>
          </div>
          <TextSizeToggle />
          {progress.completed && <Check size={20} className="text-[#10B981]" />}
        </div>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-6">
          <DayContent key={day} payload={payload} options={options} progress={progress} saving={saving} onToggle={toggle} onInteract={interact} />
        </motion.div>
      </div>
      <BottomTabs />
    </>
  );
}
